import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { RootState } from '../../../../../state/store/store';
import { connect } from 'react-redux';
import { styled } from '../../../../shared/Theme/theme';
import Button from '../../../../shared/SmallComponents/Button';
import { localizationSelectors } from '../../../../../state/ducks/localization/selectors';
import { FormDatePicker, FormDropDown, FormTextInput } from '../../../../shared/inputs/form';
import FieldLabel from '../../../../shared/SmallComponents/FieldLabel';
import { Flex } from '../../../singlePurchaseOrder/PurchaseOrderForm/PurchaseOrderForm';
import { auditSelectors } from '../../../../../state/ducks/audit/selectors';
import { AuditLogFilters } from '../../../../../models/entities/audit/auditLog';

type Props = {
    localizations: any;
    currentFilters: AuditLogFilters;
    entityTypes: string[];
    auditUsers: Array<{ userId: string; userName: string }>;
    onCancel: () => void;
    setAuditFilters: (filters: AuditLogFilters) => void;
};

type formFilterFields = {
    entityTypes?: string[];
    userIds?: string[];
    actions?: string[];
    searchTerm?: string;
    range?: string;
    startDate?: Date;
    endDate?: Date;
};

const FilterContent = ({
    localizations,
    currentFilters,
    entityTypes,
    auditUsers,
    onCancel,
    setAuditFilters
}: Props) => {
    const [formFieldsValues, setFormFieldsValues] = useState<formFilterFields>({});

    useEffect(() => {
        const filterFieldsValues: formFilterFields = {
            entityTypes: currentFilters.entityTypes,
            userIds: currentFilters.userIds,
            actions: currentFilters.actions,
            searchTerm: currentFilters.searchTerm,
            range: currentFilters.range,
            startDate: currentFilters.startDate ? new Date(currentFilters.startDate) : undefined,
            endDate: currentFilters.endDate ? new Date(currentFilters.endDate) : undefined
        };
        setFormFieldsValues(filterFieldsValues);
    }, [currentFilters]);

    const actionOptions = [
        { text: 'Created', value: 'CREATE' },
        { text: 'Updated', value: 'UPDATE' },
        { text: 'Deleted', value: 'DELETE' }
    ];

    const rangeOptions = [
        { text: 'Today', value: 'today' },
        { text: 'This Week', value: 'week' },
        { text: 'Last 30 Days', value: 'month' },
        { text: 'Last 3 Months', value: 'quarter' },
        { text: 'Last Year', value: 'year' }
    ];

    const entityTypeOptions = entityTypes.map(type => ({
        text: type,
        value: type
    }));

    const userOptions = auditUsers.map(user => ({
        text: user.userName,
        value: user.userId
    }));

    const setInitialValues = () => {
        return formFieldsValues;
    };

    const validate = (values: any) => {
        const errors: any = {};
        return errors;
    };

    const submit = (values: any) => {
        const filters: AuditLogFilters = {};

        if (formFieldsValues.entityTypes && formFieldsValues.entityTypes.length > 0) {
            filters.entityTypes = formFieldsValues.entityTypes;
        }
        if (formFieldsValues.userIds && formFieldsValues.userIds.length > 0) {
            filters.userIds = formFieldsValues.userIds;
        }
        if (formFieldsValues.actions && formFieldsValues.actions.length > 0) {
            filters.actions = formFieldsValues.actions as any;
        }
        if (formFieldsValues.searchTerm) {
            filters.searchTerm = formFieldsValues.searchTerm;
        }
        if (formFieldsValues.range) {
            filters.range = formFieldsValues.range as any;
        }
        if (formFieldsValues.startDate) {
            filters.startDate = formFieldsValues.startDate.toISOString();
        }
        if (formFieldsValues.endDate) {
            filters.endDate = formFieldsValues.endDate.toISOString();
        }

        setAuditFilters(filters);
        onCancel();
    };

    const resetForm = () => {
        setFormFieldsValues({});
        setAuditFilters({});
    };

    return (
        <Formik initialValues={setInitialValues()} validate={validate} onSubmit={submit}>
            {({ values, setFieldValue }: any) => {
                return (
                    <FormContainer>
                        <Container>
                            <FieldContainer data-popover-bug>
                                <FieldLabel>Date Range</FieldLabel>
                                <FormDropDown
                                    preventPopoverBug={true}
                                    name="range"
                                    placeHolder="Select date range"
                                    options={rangeOptions}
                                    onChange={(value: string) => {
                                        setFormFieldsValues({ ...formFieldsValues, range: value });
                                        if (value) {
                                            // Clear custom dates when selecting a range
                                            setFormFieldsValues({
                                                ...formFieldsValues,
                                                range: value,
                                                startDate: undefined,
                                                endDate: undefined
                                            });
                                        }
                                    }}
                                    value={formFieldsValues.range}
                                    allowClear
                                />
                            </FieldContainer>

                            {!formFieldsValues.range && (
                                <>
                                    <FieldContainer data-popover-bug>
                                        <FieldLabel>Start Date</FieldLabel>
                                        <FormDatePicker
                                            name="startDate"
                                            placeHolder="Start date"
                                            onChange={(value: Date) => setFormFieldsValues({ ...formFieldsValues, startDate: value })}
                                            value={formFieldsValues.startDate}
                                            preventPopoverBug={true}
                                        />
                                    </FieldContainer>
                                    <FieldContainer data-popover-bug>
                                        <FieldLabel>End Date</FieldLabel>
                                        <FormDatePicker
                                            name="endDate"
                                            placeHolder="End date"
                                            onChange={(value: Date) => setFormFieldsValues({ ...formFieldsValues, endDate: value })}
                                            value={formFieldsValues.endDate}
                                            preventPopoverBug={true}
                                        />
                                    </FieldContainer>
                                </>
                            )}

                            <FieldContainer data-popover-bug>
                                <FieldLabel>Entity Types</FieldLabel>
                                <FormDropDown
                                    preventPopoverBug={true}
                                    name="entityTypes"
                                    placeHolder="Select entity types"
                                    options={entityTypeOptions}
                                    allowClear
                                    mode="multiple"
                                    onChange={(value: string[]) => setFormFieldsValues({ ...formFieldsValues, entityTypes: value })}
                                    value={formFieldsValues.entityTypes}
                                />
                            </FieldContainer>

                            <FieldContainer data-popover-bug>
                                <FieldLabel>Actions</FieldLabel>
                                <FormDropDown
                                    preventPopoverBug={true}
                                    name="actions"
                                    placeHolder="Select actions"
                                    options={actionOptions}
                                    allowClear
                                    mode="multiple"
                                    onChange={(value: string[]) => setFormFieldsValues({ ...formFieldsValues, actions: value })}
                                    value={formFieldsValues.actions}
                                />
                            </FieldContainer>

                            <FieldContainer data-popover-bug>
                                <FieldLabel>Users</FieldLabel>
                                <FormDropDown
                                    preventPopoverBug={true}
                                    name="userIds"
                                    placeHolder="Select users"
                                    options={userOptions}
                                    allowClear
                                    mode="multiple"
                                    showSearch={true}
                                    onChange={(value: string[]) => setFormFieldsValues({ ...formFieldsValues, userIds: value })}
                                    value={formFieldsValues.userIds}
                                />
                            </FieldContainer>
                        </Container>
                        <FormFooter>
                            <Button minWidth="55px" type="button" onClick={onCancel}>
                                <span>Cancel</span>
                            </Button>
                            <Flex>
                                <Button minWidth="55px" type="button" onClick={resetForm}>
                                    <span>Reset</span>
                                </Button>
                                <Button minWidth="55px" buttonType="filled" type="submit">
                                    <span>Apply</span>
                                </Button>
                            </Flex>
                        </FormFooter>
                    </FormContainer>
                );
            }}
        </Formik>
    );
};

const FormContainer = styled(Form)`
    min-width: 500px;
    @media screen and (max-width: 768px) {
        min-width: 100%;
    }
`;

const FieldContainer = styled.div`
    margin-bottom: 12px;
    flex-basis: 45%;
    width: 0;
    padding: 0 10px;
    @media screen and (max-width: 768px) {
        flex-basis: 100%;
    }
`;

const Container = styled.div`
    width: 100%;
    max-height: 420px;
    overflow: auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    @media screen and (max-width: 768px) {
        height: calc(100vh - 70px);
        padding: 4px 12px;
        max-height: 100%;
    }
`;

const FormFooter = styled.div`
    position: absolute;
    width: 100%;
    display: flex;
    justify-content: space-between;
    height: 70px;
    padding: 17px 30px;
    border-top: 1px solid #eaeaea;
    left: 0;
    right: 0;
    bottom: -70px;
    background: white;
    box-shadow: 0 9px 12px 1px rgb(0 0 0 / 9%);
    button {
        padding: 15px;
    }
    @media screen and (max-width: 768px) {
        bottom: 0;
        button {
            span {
                display: -webkit-box;
            }
        }
    }
`;

const mapStateToProps = (state: RootState) => ({
    localizations: localizationSelectors.allPurchaseOrdersPage(state).filter, // TODO: Add audit localization
    currentFilters: auditSelectors.currentFilters(state),
    entityTypes: auditSelectors.entityTypes(state),
    auditUsers: auditSelectors.auditUsers(state)
});

const mapDispatchToProps = (dispatch: any) => ({
    setAuditFilters: (filters: AuditLogFilters) => {
        dispatch.audit.setCurrentFilters(filters);
        dispatch.audit.fetchGroupedAuditLogs(filters, false);
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterContent);
